import { ActivityState, DesignEntity } from '@/lib/enums';
import includeActivityByFilterState from '../utils/includeActivityByFilterState';
import deriveActivity from './activity/derive';
import activityTags from './activity/tags';
import deriveGroup from './group/derive';
import groupTags from './group/tags';
import derivePerson from './person/derive';
import deriveRole from './role/derive';
import roleTags from './role/tags';

/**
 * Derives default properties and metrics for every entity in the snapshot.
 * @param {Object}
 * @return {Object}
 */
export default function deriveEntities({
  activityState,
  includeTagMetrics,
  metrics,
  library,
  snapshot,
}) {
  // These sets are used to identity which entities have been missed when
  // looping through the activities first.
  const roleSet = new Set(Object.keys(snapshot.entities.roles));
  const groupSet = new Set(Object.keys(snapshot.entities.groups));
  const peopleSet = new Set(Object.keys(snapshot.entities.people));

  // The tag map is used to track entities that have been added for each tag
  // type. This assists with identifying unique entities at each level.
  const tagMap = new Map([
    [DesignEntity.ACTIVITY, new Map()],
    [DesignEntity.ROLE, new Map()],
    [DesignEntity.GROUP, new Map()],
  ]);

  // Loop through each activity and update every activity, role and group
  // meta data associated with it.
  for (const id in snapshot.entities.activities) {
    const snapshotActivity = snapshot.entities.activities[id];

    const includeActivity = includeActivityByFilterState({
      activity: snapshotActivity,
      activityState,
    });

    const activityIsUnallocated =
      snapshotActivity.owner_type !== DesignEntity.ROLE;

    // We should only derive metrics for entities when an activity state
    // matches the user's selection and if an activity belongs to a role.
    const deriveMetrics = includeActivity && !activityIsUnallocated;

    // Add default properties and normalize the activity data.
    const activity = deriveActivity({
      activityId: id,
      deriveMetrics,
      library,
      metrics,
      snapshot,
    });

    if (activity?.owner_type === DesignEntity.GROUP) {
      continue;
    }

    const person = derivePerson({
      activity,
      deriveMetrics,
      metrics,
      peopleSet,
      roleId: activity.owner_uuid,
      roleSet,
      snapshot,
    });

    const role = deriveRole({
      activity,
      deriveMetrics,
      library,
      metrics,
      roleId: activity.owner_uuid,
      roleSet,
      snapshot,
    });

    const group = deriveGroup({
      activity,
      deriveMetrics,
      groupId: role?.group_uuid,
      groupSet,
      metrics,
      role,
      roleSet,
      library,
      snapshot,
    });

    if (deriveMetrics && role && roleSet.has(role.uuid)) {
      roleSet.delete(role.uuid);
    }
    if (deriveMetrics && group && groupSet.has(group.uuid)) {
      groupSet.delete(group.uuid);
    }
    if (deriveMetrics && person && peopleSet.has(person.uuid)) {
      peopleSet.delete(person.uuid);
    }

    // Exit if we don't want to calculate tag metrics.
    if (!deriveMetrics || !includeTagMetrics) {
      continue;
    }

    // Update the activity tag metrics.
    if (activity.tags.length > 0) {
      activityTags({
        activity,
        role,
        group,
        metrics,
        tagMap,
      });
    }

    // Update the role tag metrics.
    if (role?.tags?.length) {
      roleTags({
        activity,
        role,
        group,
        metrics,
        tagMap,
      });
    }

    // Update the group tag metrics.
    if (group?.tags?.length) {
      groupTags({
        activity,
        role,
        group,
        metrics,
        tagMap,
      });
    }
  }

  // Here derive roles that didn't have any activities.
  roleSet.forEach((roleId) => {
    const deriveMetrics = activityState !== ActivityState.INACTIVE;

    const person = derivePerson({
      deriveMetrics,
      metrics,
      peopleSet,
      roleId,
      roleSet,
      snapshot,
    });

    const role = deriveRole({
      deriveMetrics,
      library,
      metrics,
      roleId,
      roleSet,
      snapshot,
    });

    const group = deriveGroup({
      deriveMetrics,
      groupId: role?.group_uuid,
      groupSet,
      metrics,
      role,
      roleSet,
      library,
      snapshot,
    });

    if (role && roleSet.has(role.uuid)) {
      roleSet.delete(role.uuid);
    }
    if (group && groupSet.has(group.uuid)) {
      groupSet.delete(group.uuid);
    }
    if (person && peopleSet.has(person.uuid)) {
      peopleSet.delete(person.uuid);
    }

    // Exit if we're not calculating tag metrics.
    if (!deriveMetrics || !includeTagMetrics) {
      return;
    }

    // Update the role tag metrics.
    if (role?.tags?.length) {
      roleTags({
        role,
        group,
        metrics,
        tagMap,
      });
    }

    // Update the group tag metrics.
    if (group?.tags?.length) {
      groupTags({
        role,
        group,
        metrics,
        tagMap,
      });
    }
  });

  // Here we derive groupsthat didn't have any roles.
  groupSet.forEach((groupId) => {
    const deriveMetrics = activityState !== ActivityState.INACTIVE;

    const group = deriveGroup({
      deriveMetrics,
      groupId,
      groupSet,
      metrics,
      roleSet,
      library,
      snapshot,
    });

    // Exit if we're not calculating tag metrics.
    if (!deriveMetrics || !includeTagMetrics) {
      return;
    }

    // Calculate the group tags.
    if (group?.tags?.length) {
      groupTags({
        group,
        metrics,
        tagMap,
      });
    }
  });

  // Here derive people that haven't been assigned to any roles.
  peopleSet.forEach((personId) => {
    const deriveMetrics = activityState !== ActivityState.INACTVE;

    // Add default properties and normalize the person data.
    derivePerson({
      deriveMetrics,
      metrics,
      peopleSet,
      personId,
      roleSet,
      snapshot,
    });
  });
}
